export const OUTLOOK_APPEARANCE_PROPERTY = "clsAppearance";
export const OUTLOOK_APPEARANCE_START = "<!-- CLS_APPEARANCE_START -->";
export const OUTLOOK_APPEARANCE_END = "<!-- CLS_APPEARANCE_END -->";

function clean(value) {
  return `${value || ""}`.trim();
}

function escapeHtml(value) {
  return clean(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function formatAppearanceSubject(appearance) {
  const clientName = clean(appearance?.clientName) || "Client";
  const appearanceType = clean(appearance?.appearanceType) || "Court appearance";
  return `${clientName} - ${appearanceType}`;
}

export function formatAppearanceLocation(appearance) {
  return [appearance?.court, appearance?.listingLocation]
    .map(clean)
    .filter(Boolean)
    .join(" - ");
}

export function buildMicrosoftCalendarEvent(appearance) {
  const date = clean(appearance?.date);
  if (!date) throw new Error("A calendar date is required.");

  const startTime = clean(appearance?.startTime) || "09:00";
  const endTime = clean(appearance?.endTime) || "10:00";
  return {
    subject: formatAppearanceSubject(appearance),
    body: {
      contentType: "HTML",
      content: buildAppearanceHtml(appearance),
    },
    start: {
      dateTime: `${date}T${startTime}:00`,
      timeZone: "AUS Eastern Standard Time",
    },
    end: {
      dateTime: `${date}T${endTime}:00`,
      timeZone: "AUS Eastern Standard Time",
    },
    location: {
      displayName: formatAppearanceLocation(appearance),
    },
  };
}

export function createNextAppearanceSnapshot(currentAppearance, {
  date,
  startTime = "09:00",
  endTime = "10:00",
  appearanceType = "Mention",
} = {}) {
  return {
    ...currentAppearance,
    date: clean(date),
    startTime: clean(startTime) || "09:00",
    endTime: clean(endTime) || "10:00",
    appearanceType: clean(appearanceType) || "Mention",
    outcome: "",
    notes: "",
    nextCourtDate: "",
    nextCourtTime: "09:00",
    nextAppearanceType: "Mention",
  };
}

export function buildActionstepOutcomeNote(appearance) {
  const lines = [
    `Court appearance: ${formatAppearanceSubject(appearance)}`,
    appearance?.fileNumber ? `Matter: ${clean(appearance.fileNumber)}` : "",
    appearance?.date ? `Date: ${clean(appearance.date)}` : "",
    appearance?.court ? `Court: ${clean(appearance.court)}` : "",
    appearance?.listingLocation ? `Listed at: ${clean(appearance.listingLocation)}` : "",
    appearance?.fundingType ? `Funding type: ${clean(appearance.fundingType)}` : "",
    appearance?.fundingType === "Legal Aid" && appearance?.grantOfAidNumber
      ? `Grant of aid number: ${clean(appearance.grantOfAidNumber)}`
      : "",
    appearance?.nextCourtDate ? `Next court date: ${clean(appearance.nextCourtDate)}` : "",
    appearance?.nextCourtTime ? `Next court time: ${clean(appearance.nextCourtTime)}` : "",
    appearance?.nextAppearanceType ? `Next appearance: ${clean(appearance.nextAppearanceType)}` : "",
    appearance?.outcome ? `Outcome: ${clean(appearance.outcome)}` : "",
    appearance?.notes ? `Notes: ${clean(appearance.notes)}` : "",
  ];

  return lines.filter(Boolean).join("\n");
}

export function buildAppearanceHtml(appearance) {
  const rows = [
    ["Client", appearance?.clientName],
    ["Matter", appearance?.fileNumber],
    ["Appearance", appearance?.appearanceType],
    ["Court", appearance?.court],
    ["Listed at", appearance?.listingLocation],
    ["Funding", appearance?.fundingType],
    [
      "Grant of aid",
      appearance?.fundingType === "Legal Aid" ? appearance?.grantOfAidNumber : "",
    ],
    ["Matter to be briefed", appearance?.matterToBeBriefed ? "Yes" : ""],
    ["Next court date", appearance?.nextCourtDate],
    ["Next court time", appearance?.nextCourtTime],
    ["Next appearance", appearance?.nextAppearanceType],
    ["Outcome", appearance?.outcome],
    ["Notes", appearance?.notes],
  ].filter(([, value]) => clean(value));

  const tableRows = rows
    .map(
      ([label, value]) =>
        `<tr><td style="padding:4px 12px 4px 0;color:#64748b;font-weight:600;vertical-align:top;">${escapeHtml(label)}</td><td style="padding:4px 0;color:#1e293b;">${escapeHtml(value)}</td></tr>`
    )
    .join("");

  // The snapshot travels with the appointment, not just a table a person can read.
  //
  // Without it, editing an appointment lost the Actionstep matter: the visible table
  // carries the client's name but never the matter id, so the link could not be rebuilt
  // and the lawyer had to find the matter again every time they opened the appointment.
  const payload = escapeHtml(JSON.stringify(appearance || {}));

  return `${OUTLOOK_APPEARANCE_START}
<div data-cls-appearance="true" data-cls-snapshot="${payload}" style="font-family:'Segoe UI',Arial,sans-serif;border:1px solid #d8b4fe;border-left:4px solid #7e22ce;border-radius:8px;padding:14px;margin:12px 0;background:#faf5ff;max-width:620px;">
  <div style="font-size:13px;font-weight:700;color:#581c87;margin-bottom:8px;">Complete Law Support - Actionstep appearance</div>
  <table style="border-collapse:collapse;font-size:12px;">${tableRows}</table>
</div>
${OUTLOOK_APPEARANCE_END}`;
}

export function mergeAppearanceHtml(existingHtml, appearance) {
  const current = `${existingHtml || ""}`;
  const block = buildAppearanceHtml(appearance);
  const withoutMarkedBlocks = current.replace(
    /<!--\s*CLS_APPEARANCE_START\s*-->[\s\S]*?<!--\s*CLS_APPEARANCE_END\s*-->/gi,
    ""
  );
  let remainder = withoutMarkedBlocks;

  if (typeof DOMParser !== "undefined") {
    const document = new DOMParser().parseFromString(withoutMarkedBlocks, "text/html");
    document.querySelectorAll('[data-cls-appearance="true"]').forEach((node) => node.remove());
    [...document.querySelectorAll("div")]
      .filter((node) => /^Complete Law Support\s*[-–]\s*Actionstep appearance$/i.test(node.textContent.trim()))
      .forEach((title) => {
        const card = title.parentElement;
        if (card?.querySelector("table")) card.remove();
      });
    remainder = document.body.innerHTML;
  } else {
    remainder = withoutMarkedBlocks.replace(
      /<div\b[^>]*data-cls-appearance=(?:"true"|'true'|true)[^>]*>\s*<div\b[^>]*>[\s\S]*?Complete Law Support\s*-\s*Actionstep appearance[\s\S]*?<\/div>\s*<table\b[\s\S]*?<\/table>\s*<\/div>/gi,
      ""
    ).replace(
      /<div\b[^>]*>\s*<div\b[^>]*>\s*Complete Law Support\s*[-–]\s*Actionstep appearance\s*<\/div>\s*<table\b[\s\S]*?<\/table>\s*<\/div>/gi,
      ""
    );
  }

  remainder = remainder
    .replace(/(?:\s*<br\s*\/?>(?:\s|&nbsp;)*){2,}/gi, "<br>")
    .replace(/^(?:\s*<br\s*\/?>\s*)+/i, "")
    .trim();
  return `${block}${remainder ? `<br>${remainder}` : ""}`;
}

export function createAppearanceSnapshot(matter, fields) {
  return {
    version: 1,
    actionstepMatterId: clean(matter?.externalId),
    clientName: clean(matter?.client?.fullName),
    fileNumber: clean(matter?.client?.fileNumber),
    appearanceType: clean(fields?.appearanceType),
    date: clean(fields?.date),
    startTime: clean(fields?.startTime),
    endTime: clean(fields?.endTime),
    court: clean(fields?.court),
    listingLocation: clean(fields?.listingLocation),
    fundingType: clean(fields?.fundingType),
    grantOfAidNumber:
      clean(fields?.fundingType) === "Legal Aid" ? clean(fields?.grantOfAidNumber) : "",
    matterToBeBriefed: Boolean(fields?.matterToBeBriefed),
    counselListMethod: clean(fields?.counselListMethod),
    clerkEmail: clean(fields?.clerkEmail),
    briefingAssigneeRole: clean(fields?.briefingAssigneeRole),
    briefingNotes: clean(fields?.briefingNotes),
    nextCourtDate: clean(fields?.nextCourtDate),
    nextCourtTime: clean(fields?.nextCourtTime),
    nextAppearanceType: clean(fields?.nextAppearanceType),
    outcome: clean(fields?.outcome),
    notes: clean(fields?.notes),
    matter: matter
      ? {
          externalId: clean(matter.externalId),
          client: {
            fullName: clean(matter.client?.fullName),
            fileNumber: clean(matter.client?.fileNumber),
          },
          matter: {
            court: clean(matter.matter?.court),
            appearanceType: clean(matter.matter?.appearanceType),
            nextAppearanceDate: clean(matter.matter?.nextAppearanceDate),
            fundingType: clean(matter.matter?.fundingType),
            grantOfAidNumber: clean(matter.matter?.grantOfAidNumber),
          },
        }
      : null,
    updatedAt: new Date().toISOString(),
  };
}

function unescapeHtml(value) {
  return `${value || ""}`
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    // Ampersand last: doing it first would turn "&amp;lt;" into "<" rather than "&lt;".
    .replace(/&amp;/g, "&");
}

// Reads back the snapshot written into an appointment body by buildAppearanceHtml.
//
// This is what lets an appointment keep its Actionstep matter when it is reopened. The
// visible table was never enough: it shows the client's name but not the matter id, so the
// link could be read by a person and not by the portal.
//
// Returns null when there is nothing to read, so a caller can tell an appointment that
// carries no appearance from one that carries an empty one.
export function readMicrosoftAppearance(event) {
  const html = `${event?.body?.content || event?.body || ""}`;
  if (!html) return null;

  const match = html.match(/data-cls-snapshot="([^"]*)"/);
  if (!match) return null;

  // Appointments created before the snapshot existed carry no data-cls-snapshot at all.
  // They are not broken, they simply have nothing structured to give back.
  const raw = unescapeHtml(match[1]);
  if (!raw || raw === "true") return null;

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    // A body mangled by a mail client is not worth guessing at; the fields stay as they are.
    return null;
  }
}
